import { useEffect, useState } from "react";
import type { AttendanceRecord, ManagedUser } from "@platform/contracts";
import { api } from "../api";
import type { NotificationKind } from "./Portal";
import { useLiveData } from "../useLiveData";

function isoDate(date: Date): string { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`; }

export function PunchesPanel({ canManageUsers, onMessage }: { canManageUsers: boolean; onMessage: (message: string, kind?: NotificationKind) => void }) {
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [userId, setUserId] = useState("");
  const [from, setFrom] = useState(() => isoDate(new Date(Date.now() - 6 * 86400000)));
  const [to, setTo] = useState(() => isoDate(new Date()));
  const [loading, setLoading] = useState(false);
  const report = (error: Error) => onMessage(error.message, "error");
  const load = async () => { setLoading(true); try { setRecords((await api.attendance({ userId: userId || undefined, from, to })).records); } finally { setLoading(false); } };
  useEffect(() => { load().catch(report); }, [userId, from, to]);
  useEffect(() => { if (canManageUsers) api.users().then((result) => setUsers(result.users)).catch(() => setUsers([])); }, [canManageUsers]);
  useLiveData(load);

  const open = records.filter((record) => !record.punchedOutAt).length;
  const totalMinutes = records.reduce((sum, record) => sum + (record.minutes ?? 0), 0);
  const duration = (minutes: number) => `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
  const staffOptions = users.filter((user) => user.identificationNumber || records.some((record) => record.userId === user.id));

  return <section className="punches-panel">
    <h2>Punches</h2>
    <p>Attendance records from the punch terminal. Filter by date range or person.</p>
    <div className="table-tools order-range-tools">
      <label className="range-field"><span>From</span><input type="date" value={from} onChange={(event) => setFrom(event.target.value)}/></label>
      <label className="range-field"><span>To</span><input type="date" value={to} onChange={(event) => setTo(event.target.value)}/></label>
      <select aria-label="Filter by user" value={userId} onChange={(event) => setUserId(event.target.value)}>
        <option value="">All people</option>
        {staffOptions.map((user) => <option key={user.id} value={user.id}>{user.displayName}{user.identificationNumber ? ` · ${user.identificationNumber}` : ""}</option>)}
      </select>
      <button className="secondary-button" disabled={loading} onClick={() => load().catch(report)}>{loading ? "Loading…" : "Refresh"}</button>
    </div>
    <div className="statement-summary">
      <div><small>Sessions</small><strong>{records.length}</strong></div>
      <div><small>Still punched in</small><strong>{open}</strong></div>
      <div><small>Total hours</small><strong>{duration(totalMinutes)}</strong></div>
    </div>
    <div className="data-table"><table>
      <thead><tr><th>Date</th><th>Person</th><th>ID</th><th>Role</th><th>Punch in</th><th>Punch out</th><th className="num-cell">Duration</th></tr></thead>
      <tbody>{records.map((record) => <tr key={record.id}>
        <td>{new Date(`${record.punchedInAt}Z`).toLocaleDateString()}</td>
        <td><strong>{record.displayName}</strong></td>
        <td>{record.identificationNumber ?? "—"}</td>
        <td>{record.role.replaceAll("_", " ")}</td>
        <td>{new Date(`${record.punchedInAt}Z`).toLocaleTimeString()}</td>
        <td>{record.punchedOutAt ? new Date(`${record.punchedOutAt}Z`).toLocaleTimeString() : <span className="status new">on shift</span>}</td>
        <td className="num-cell">{record.minutes === null ? "—" : duration(record.minutes)}</td>
      </tr>)}
      {records.length === 0 && <tr><td colSpan={7} className="empty-cell">No punches in this period.</td></tr>}</tbody>
    </table></div>
  </section>;
}
